// Passes: versioned pass products, holdings, and the actions on them (F-PRC-01 … F-PRC-07; spec/06 §3).
//
// A **pass product** is play time on one or more station classes, sold as a ticket. It is
// **versioned**: editing its price, duration, classes or windows makes a new version, and a holding
// keeps the version it was sold under (spec/06 §3.1). A **pass holding** is the seconds one player
// holds; its remaining seconds move only through the double-entry ledger (spec/06 §2, §3.2), so no
// column holds a balance.
//
// This file is the venue console's Passes screen and its actions:
//
//   GET   …/passes/products                       list the pass catalogue (F-PRC-01)          passes:read
//   POST  …/passes/products                       make a pass product                          passes:manage
//   GET   …/passes/products/{productId}           read one, with its live version              passes:read
//   PATCH …/passes/products/{productId}           edit it — a new version if terms changed      passes:manage
//   POST  …/passes/products/{productId}/sell      sell a paid holding to a player               orders:create
//   POST  …/passes/products/{productId}/grant     staff grant a free holding, with a reason     passes:grant
//   GET   …/passes/holdings                        list holdings (by product, player, status)   passes:read
//   POST  …/passes/holdings/{holdingId}/refund    refund a holding by its rules                 passes:refund
//   POST  …/passes/holdings/{holdingId}/void      void a holding, removing unused seconds        passes:void
//
// Money moves through the ledger and every sell, grant, refund and void writes one audit row (the
// journal-entry audit guard refuses a value movement without one, spec/06 §2.2, ADR-0013). Tax is
// carried but not charged yet: the foundation venue is `unregistered`, so a pass sale is the whole
// price to `sales_play` (spec/06 §10.3 rule 2), until the tax engine lands (F-PAY-11). Every DTO name
// is prefixed `Pass…` so its generated C# type does not collide with a hand-written station type.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { PassHoldingIdSchema, PassProductIdSchema, PassProductVersionIdSchema, PassPurchaseIdSchema, PlayerIdSchema, SaleIdSchema, StationClassIdSchema, VenueIdSchema, } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { MoneySchema, NonNegativeMoneySchema, PositiveMoneySchema } from "../primitives/money.js";
import { paginated, PageQuerySchema } from "../primitives/page.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { TimeWindowSchema } from "../primitives/time-window.js";
import { ORDERS_CREATE, PASSES_GRANT, PASSES_MANAGE, PASSES_READ, PASSES_REFUND, PASSES_VOID } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { IdempotentCreateHeaderSchema } from "./id-checks.js";
// ---------------------------------------------------------------------------------------------
// Shared vocabulary (spec/06 §3.1)
// ---------------------------------------------------------------------------------------------
/** Where a duration is bounded (spec/06 §3.1): 15 min to 30 days of play. */
export const PASS_MIN_DURATION_SECONDS = 900;
export const PASS_MAX_DURATION_SECONDS = 2_592_000;
/** A display and filtering label; ordering uses windows, not labels (spec/06 §3.1). */
export const PassLabelSchema = z
    .enum(['standard', 'day', 'night', 'weekend', 'special', 'limited'])
    .openapi('PassLabel', { description: 'A pass’s display label; a limited-time offer is `limited` (spec/06 §3.1, F-PRC-07).' });
/** Where a pass is sold (spec/06 §3.1). */
export const PassChannelSchema = z
    .enum(['counter', 'kiosk', 'app', 'web'])
    .openapi('PassChannel', { description: 'A channel a pass is sold on (spec/06 §3.1).' });
/** The tenders the foundation takes for a pass sale or returns a refund to (spec/06 §9.1; `online` follows). */
export const PassTenderSchema = z
    .enum(['cash', 'counter_card', 'counter_upi'])
    .openapi('PassTender', { description: 'How a pass sale is paid, or a refund returned (spec/06 §9.1). `online` is a follow-on.' });
/** The three kinds of pass scope (spec/06 §3.1, §3.6). */
export const PassScopeKindSchema = z.enum(['venues', 'organization', 'network']).openapi('PassScopeKind');
/**
 * Where a pass funds play (spec/06 §3.1, §3.6). `venues` lists the organisation's venues it works at
 * (`venueIds` required, and present only then); `organization` is all of them; `network` is every
 * venue of the organisation's network (P2). A flat shape rather than a discriminated union, because
 * the generated clients do not model `oneOf`; the server enforces that `venueIds` accompanies
 * `venues` and no other kind, and that every venue named is one of the organisation's.
 *
 * The owner's choice (2026-09-30, #709) is **organisation-wide or a single venue**: the console offers
 * exactly those two (`organization`, or `venues` with one venue picked). A holding keeps the scope of the
 * version it was sold under, so it is used only where that scope reaches — refused elsewhere, and not
 * listed on another venue's kiosk.
 */
export const PassScopeSchema = z
    .object({
    type: PassScopeKindSchema,
    /** The venues, for `type: venues` only; at least one there, absent otherwise (spec/06 §3.1). */
    venueIds: z.array(VenueIdSchema).min(1).max(200).optional(),
})
    .strict()
    .openapi('PassScope', { description: 'Where a pass may be used: listed venues, the whole organisation, or its network (spec/06 §3.1).' });
/** How a holding's expiry is set (spec/06 §3.1). */
export const PassValidityKindSchema = z
    .enum(['days_after_purchase', 'days_after_first_use', 'fixed_end'])
    .openapi('PassValidityKind');
/**
 * When a holding stops being valid (spec/06 §3.1). `days` accompanies the two day-based kinds (1–730),
 * `endsAt` the `fixed_end` kind; the server enforces the pairing. Flat, for the same reason as the
 * scope above.
 */
export const PassValiditySchema = z
    .object({
    type: PassValidityKindSchema,
    days: z.int().min(1).max(730).optional(),
    endsAt: IsoDateTimeSchema.optional(),
})
    .strict()
    .openapi('PassValidity', { description: 'How a holding’s expiry is set (spec/06 §3.1).' });
/** An optional limited-time selling window (spec/06 §3.1, F-PRC-07). */
export const PassSaleWindowSchema = z
    .object({
    availableFrom: IsoDateTimeSchema.optional(),
    availableUntil: IsoDateTimeSchema.optional(),
})
    .strict()
    .openapi('PassSaleWindow', { description: 'When a limited-time pass may be sold (spec/06 §3.1).' });
/** Optional per-player purchase limits (spec/06 §3.1). */
export const PassPurchaseLimitsSchema = z
    .object({
    perPlayerPerDay: z.int().min(1).max(1000).optional(),
    perPlayerTotal: z.int().min(1).max(100_000).optional(),
})
    .strict()
    .openapi('PassPurchaseLimits', { description: 'How many of this pass one player may buy (spec/06 §3.1).' });
/**
 * The editable **terms** of a pass product — the fields that a new version snapshots (spec/06 §3.1).
 * Everything a holding is sold under lives here, so a holding keeps its version's terms unchanged.
 */
export const PassProductTermsSchema = z
    .object({
    name: z.string().trim().min(1).max(40),
    /** For a kiosk ticket (spec/06 §3.1). */
    shortName: z.string().trim().min(1).max(12),
    description: z.string().trim().max(280).optional(),
    /** ≥ 0; 0 is allowed only for a staff-only (grant-only) product (spec/06 §3.1). */
    price: NonNegativeMoneySchema,
    /**
     * The tax category (F-PAY-11): the `txc_…` id of one of the organisation's tax categories
     * (`GET …/tax-categories`), carried on the version. **Recorded, not applied**: a pass line stays
     * untaxed until the owner decides GST on passes (#709), so a category can be attached ahead of
     * that decision without any change to the catalogue.
     */
    taxCategoryId: z.string().trim().min(1).max(64).optional(),
    durationSeconds: z.int().min(PASS_MIN_DURATION_SECONDS).max(PASS_MAX_DURATION_SECONDS),
    validity: PassValiditySchema,
    /** The classes the pass funds; at least one (spec/06 §3.1). */
    stationClassIds: z.array(StationClassIdSchema).min(1).max(50),
    scope: PassScopeSchema,
    /** 0–7 windows; empty means any time (spec/06 §3.1). */
    windows: z.array(TimeWindowSchema).max(7),
    label: PassLabelSchema,
    /** Where it is sold; at least one channel. */
    channels: z.array(PassChannelSchema).min(1).max(4),
    saleWindow: PassSaleWindowSchema.optional(),
    purchaseLimits: PassPurchaseLimitsSchema.optional(),
    /** Membership plan ids that may buy it (spec/06 §3.1); memberships are a follow-on (F-MEM). */
    membersOnly: z.array(z.string().trim().min(1).max(64)).max(50).optional(),
})
    .strict();
/** One version of a pass product — the terms as they were when a holding under it was sold. */
export const PassProductVersionSchema = PassProductTermsSchema.extend({
    id: PassProductVersionIdSchema,
    productId: PassProductIdSchema,
    /** The version number, 1 upward; a new one is minted on every terms change. */
    version: z.int().min(1),
    createdAt: IsoDateTimeSchema,
}).openapi('PassProductVersion', { description: 'A pass product’s terms at a point in time (spec/06 §3.1).' });
/** A pass product, with its live version and the display fields that are not versioned. */
export const PassProductSchema = z
    .object({
    id: PassProductIdSchema,
    /** The live version — the terms a new sale or grant uses. */
    version: PassProductVersionSchema,
    /** Inactive products cannot be sold or granted (spec/06 §3.1). */
    active: z.boolean(),
    sortOrder: z.int().min(0).max(100_000),
    /** A `--clavian-*` accent token for the ticket card (spec/06 §3.1, spec/13 §12); none = the class colour. */
    accentToken: z.string().trim().min(1).max(64).nullable(),
    /** How many holdings of this product were granted or sold at this venue today (the Passes row count). */
    soldToday: z.int().min(0),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('PassProduct', { description: 'A pass product in the catalogue (spec/06 §3.1, F-PRC-01).' });
/** A holding's lifecycle (spec/06 §3.2). */
export const PassHoldingStatusSchema = z
    .enum(['active', 'exhausted', 'expired', 'refunded', 'voided'])
    .openapi('PassHoldingStatus', { description: 'A pass holding’s state (spec/06 §3.2).' });
/** A pass holding — the seconds one player holds under one product version (spec/06 §3.2, F-PRC-02). */
export const PassHoldingSchema = z
    .object({
    id: PassHoldingIdSchema,
    playerId: PlayerIdSchema,
    productId: PassProductIdSchema,
    productVersionId: PassProductVersionIdSchema,
    /** The product's name at the version sold, for a list that reads without a second lookup. */
    name: z.string(),
    grantedSeconds: z.int().min(0),
    /** Derived from the ledger (spec/06 §3.2). */
    remainingSeconds: z.int().min(0),
    /** What was paid for it; zero for a staff grant. */
    price: NonNegativeMoneySchema,
    status: PassHoldingStatusSchema,
    purchasedAt: IsoDateTimeSchema,
    /** Null until the holding first funds play. */
    firstUsedAt: IsoDateTimeSchema.nullable(),
    /** Null for a `days_after_first_use` holding not yet used (spec/06 §3.2). */
    expiresAt: IsoDateTimeSchema.nullable(),
    /**
     * Where the holding may be used: the scope of the version it was sold under (spec/06 §3.1, §3.3) —
     * organisation-wide, or only at the venue(s) named. The desk marks a holding that is not valid at its venue.
     */
    scope: PassScopeSchema,
})
    .openapi('PassHolding', { description: 'A pass a player holds (spec/06 §3.2, F-PRC-02).' });
export const PassProductListSchema = paginated(PassProductSchema, 'PassProductList');
export const PassHoldingListSchema = paginated(PassHoldingSchema, 'PassHoldingList');
// ---------------------------------------------------------------------------------------------
// The station pass shop (F-KIO-10): what the kiosk's Time screen shows, device-authenticated.
// ---------------------------------------------------------------------------------------------
//
// The kiosk shows the venue's buyable passes from the PC (spec/12 §7.1), so the station pulls the shop the
// player can buy against this station's class, device-authenticated, and hands it to the kiosk over the
// WebView2 bridge — the pass twin of `getStationMenu`. Display data: a purchase is priced server-side, so
// the shop carries no signature. The readable `window`/`validity`/`appliesTo` are the venue's words,
// resolved server-side from the product's terms and the venue's timezone. Memberships and top-ups have no
// service yet (F-MEM, F-KIO-14), so they come back empty for now; `memberPrice`/`bestValue` have no source
// and are `null`/`false`.
/** A pass's daily usable window in the venue's local hours, for the kiosk card's day strip (F-KIO-10). */
export const StationPassWindowBandSchema = z
    .object({ startHour: z.int().min(0).max(24), endHour: z.int().min(0).max(24) })
    .openapi('StationPassWindowBand', { description: 'A pass’s usable hours `[startHour, endHour)` in the venue’s local time (F-KIO-10).' });
/** One buyable pass on the kiosk Time screen (F-KIO-10), from a pass product's live terms. */
export const StationPassSchema = z
    .object({
    id: PassProductIdSchema,
    /** The pass name, in the venue's words ("1 hour", "Night pack"). */
    name: z.string(),
    /** When it can be used, in the venue's words ("Any time", "10 pm to 6 am"). */
    window: z.string(),
    durationSeconds: z.int().min(0),
    /** How long the pass itself stays valid ("Valid 30 days"). */
    validity: z.string(),
    /** The station class(es) it applies to, in the venue's words ("Standard PCs"). */
    appliesTo: z.string(),
    price: NonNegativeMoneySchema,
    /** No member pricing yet (F-MEM): always `null`. */
    memberPrice: NonNegativeMoneySchema.nullable(),
    /** No "best value" merchandising flag yet: always `false`. */
    bestValue: z.boolean(),
    /** The daily usable window in the venue's local hours, for the card's day strip; `null` = any time. */
    windowBand: StationPassWindowBandSchema.nullable(),
})
    .openapi('StationPass', { description: 'A buyable pass for the kiosk Time screen (F-KIO-10).' });
/** A membership plan on the kiosk Time screen (F-KIO-10); none exist yet (F-MEM), so the list is empty. */
export const StationPassMembershipSchema = z
    .object({ id: z.string(), name: z.string(), line: z.string(), price: NonNegativeMoneySchema, period: z.string() })
    .openapi('StationPassMembership', { description: 'A membership plan for the kiosk Time screen (F-KIO-10, F-MEM).' });
/**
 * A wallet top-up amount on the kiosk Time screen's Top up tab (F-KIO-10): the venue's `wallet.topupPresets` within the
 * minimum and maximum — or, with none set (the default), the organisation's smallest top-up and each `wallet.topupOffers`
 * threshold, as before — cheapest first, with the bonus that amount earns by the one tier rule (`@clavian/domain`
 * `bestTopUpTier`; `null` for none), the bonus a kiosk top-up of it is credited. `id` is that tier's id, or
 * `amount-<minor>` when it earns none — two amounts may earn the same tier, so a screen keys tiles by amount.
 * Empty when the wallet is off or the player on the session cannot top up here.
 */
export const StationPassTopUpSchema = z
    .object({ id: z.string(), amount: NonNegativeMoneySchema, bonus: NonNegativeMoneySchema.nullable() })
    .openapi('StationPassTopUp', { description: 'A wallet top-up amount for the kiosk Time screen (F-KIO-10).' });
/** One top-up bonus tier for the kiosk (F-WAL-03, F-KIO-10): add at least `minTopup`, get `bonus` on top. */
export const StationTopUpOfferSchema = z
    .object({ id: z.string(), minTopup: NonNegativeMoneySchema, bonus: NonNegativeMoneySchema })
    .openapi('StationTopUpOffer', { description: 'A wallet top-up bonus tier, for the kiosk (F-WAL-03, F-KIO-10).' });
/**
 * What a kiosk top-up may be (F-KIO-10, spec/06 §5): the organisation's `wallet.minTopupAmount`,
 * `wallet.maxTopupAmount` and `wallet.maxBalanceAmount`, what the player's wallet holds now, and how it can be paid
 * here — at the desk always, by UPI when online payments are open at the venue. The server re-checks every limit.
 */
export const StationTopUpLimitsSchema = z
    .object({
    min: NonNegativeMoneySchema,
    max: NonNegativeMoneySchema,
    maxBalance: NonNegativeMoneySchema,
    /** The wallet's cash + bonus now, for "up to ₹X more". */
    balance: NonNegativeMoneySchema,
    desk: z.boolean(),
    upi: z.boolean(),
    /**
     * The bonus tiers in force at this venue (`wallet.topupOffers`, the venue's own list where it has one), so the kiosk
     * shows the bonus an "Other amount" earns before the player confirms — by the same rule the server credits with
     * (2026-10-01). Optional and never null: absent from a server that predates it (the kiosk then shows no bonus for a
     * typed amount), and the station's pipe omits an absent member, so the agent never requires it.
     */
    offers: z.array(StationTopUpOfferSchema).optional(),
    /**
     * "Scan & pay (UPI)" (F-PAY-02, F-KIO-10, 2026-10-01): the venue has its own UPI ID (`payments.counterUpiVpa`), so the
     * kiosk may offer a desk top-up paid by scanning the venue's QR at the PC, which the desk then confirms. Absent (an
     * older server) or `false`: not offered.
     */
    scanToPay: z.boolean().optional(),
})
    .openapi('StationTopUpLimits', { description: 'The limits and tenders of a kiosk wallet top-up (F-KIO-10).' });
/** Pay-as-you-go on the kiosk Time screen (F-KIO-10): the per-hour rate and the wallet-fallback default. */
/** Named before its nullable use (NAMED_BEFORE_THEIR_NULLABLE_USE, #450). */
export const StationPassPaygChangeSchema = z.object({ at: IsoDateTimeSchema, rate: NonNegativeMoneySchema }).openapi('StationPassPaygChange');
export const StationPassPaygSchema = z
    .object({
    rate: NonNegativeMoneySchema,
    appliesTo: z.string(),
    walletFallbackOn: z.boolean(),
    /** The class's daily cap on the venue's rate card, or `null` when it has none (spec/06 §4, F-PRC-12). */
    dailyCap: NonNegativeMoneySchema.nullable(),
    /**
     * When the rate next changes within the day, and to what — "₹60/h now · ₹40/h from 01:00" (spec/06 §4, F-PRC-11).
     * Absent or null when a time-of-day band does not change it in the next 24 hours.
     */
    nextChange: StationPassPaygChangeSchema.nullable().optional(),
})
    .openapi('StationPassPayg', { description: 'The pay-as-you-go rate for the kiosk Time screen (F-KIO-10).' });
/** Whether a kiosk pass purchase is possible on this station right now, and why not (F-KIO-14). */
export const StationPassPurchaseBlockSchema = z
    .enum(['none', 'no_session', 'guest'])
    .openapi('StationPassPurchaseBlock', { description: 'Why a kiosk pass purchase is not possible right now (F-KIO-14).' });
/**
 * How the player on this station's session can pay for a pass here (F-KIO-14), read with the shop.
 * `blocked` is `none` when a purchase is possible, else why not: `no_session`, or `guest` — the player on the
 * session has no registered account, so passes are bought at the front desk. Each tender flag says whether it
 * is offered: `wallet` when the organisation runs wallets; `desk` whenever a purchase is possible; `upi` when online payments are open at the venue; `onAccount` when on account is on, the player
 * is registered and has credit left. `availableCredit` is what the player may still charge to their tab
 * (limit minus what they owe), or `null` when on account is not offered. Only passes in `passesHere` can be
 * bought on this station.
 */
export const StationPassPurchaseOptionsSchema = z
    .object({
    blocked: StationPassPurchaseBlockSchema,
    wallet: z.boolean(),
    desk: z.boolean(),
    upi: z.boolean(),
    onAccount: z.boolean(),
    availableCredit: NonNegativeMoneySchema.nullable(),
    /**
     * "Scan & pay (UPI)" (F-PAY-02, F-KIO-14, 2026-10-01): offered with `desk` when the venue has its own UPI ID
     * (`payments.counterUpiVpa`). Absent (an older server) or `false`: not offered.
     */
    scanToPay: z.boolean().optional(),
})
    .openapi('StationPassPurchaseOptions', { description: 'How a kiosk pass can be paid for on this station now (F-KIO-14).' });
/** The kiosk Time screen's shop (spec/12 §7.1, F-KIO-10): the venue's buyable passes and the PAYG rate. */
export const StationPassShopSchema = z
    .object({
    /** Passes for this station's class ("For this PC"), cheapest first. */
    passesHere: z.array(StationPassSchema),
    /** Passes for other classes ("Other areas"), not usable on this PC, cheapest first. */
    passesOtherAreas: z.array(StationPassSchema),
    memberships: z.array(StationPassMembershipSchema),
    topUps: z.array(StationPassTopUpSchema),
    /**
     * The limits and tenders of a kiosk top-up for the player on the session (F-KIO-10); `null` when the wallet is off
     * here, or there is no registered player on the session — the Top up tab then says "At the front desk".
     */
    topUp: StationTopUpLimitsSchema.nullable(),
    /** The pay-as-you-go rate for this station's class; `null` when the venue has pay as you go off (`sessions.payAsYouGoEnabled`). */
    payAsYouGo: StationPassPaygSchema.nullable(),
    /** How the player on this station's session can pay for a pass here, right now (F-KIO-14). */
    purchase: StationPassPurchaseOptionsSchema,
})
    .openapi('StationPassShop', { description: 'The venue’s pass shop for a station’s kiosk (F-KIO-10).' });
export const getStationPassShopRoute = createRoute({
    method: 'get',
    path: '/v1/station/passes',
    operationId: 'getStationPassShop',
    tags: ['passes', 'stations'],
    summary: 'The station reads its venue’s pass shop (F-KIO-10)',
    description: 'Device-authenticated. Returns the venue’s buyable passes for the kiosk Time screen — passes for this ' +
        'station’s class ("For this PC") and for other classes ("Other areas"), plus the pay-as-you-go rate. ' +
        'Memberships and top-ups have no service yet, so they come back empty. Display data: a purchase is ' +
        'priced server-side. The station provides its own venue and class from the device token.',
    responses: {
        200: jsonResponse(StationPassShopSchema, 'The venue’s pass shop for this station.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Buying a pass from the kiosk (F-KIO-14 for passes, F-KIO-10; spec/12 §7.1, §7.4; spec/06 §8.2, §9.1)
// ---------------------------------------------------------------------------------------------
//
// The player on a station's session buys a pass on the kiosk Time screen. The station names only the
// product and how the player pays; the server derives the player from the station's own active session
// (never a player id from the client) and prices the pass from the product's live version (the kiosk never
// sends a price). A purchase is an ordinary sale with `channel: kiosk` — one `time` line, with the station,
// session and player on it — so it shows in the console's Orders like any other; the pass holding is issued
// and queued onto the session only once the sale is paid:
//
//   wallet       the player's wallet pays now (`wallet.enabled`); the pass is issued and queued at once.
//   on_account   "Add to my tab": the sale is charged to the player's receivable (`credit.enabled`, a
//                registered player, within their available credit); issued and queued at once.
//   desk         an unpaid kiosk order waits in Orders ("Awaiting payment"); the pass is issued and queued
//                when staff take payment at the counter, and nothing happens if they cancel it.
//   upi          a payment intent for the exact amount through the venue's online provider; the kiosk
//                shows its QR, and the pass is issued and queued when the provider confirms the capture.
//                Offered only when online payments are open at the venue (`payments.onlineEnabled` and a
//                configured provider).
//
// The station learns what became of a pending purchase from the `station.pass.purchase` command, and
// reads it back with `GET …/purchases/{purchaseId}` (which also re-sends the session lease once applied).
/** How the player pays for a pass bought at the kiosk (F-KIO-14). There is no postpaid "add to bill". */
export const StationPassTenderSchema = z
    .enum(['wallet', 'desk', 'upi', 'on_account'])
    .openapi('StationPassTender', { description: 'How a kiosk pass purchase is paid (F-KIO-14).' });
/**
 * Where a kiosk pass purchase is (F-KIO-14). `awaiting_desk` and `awaiting_upi` are pending: nothing is
 * applied. `applied` once the sale is paid and the pass is issued (and queued onto the session when it can
 * fund play here now). `cancelled` when staff or the player cancelled it; `expired` when the UPI request
 * ran out before it was paid.
 */
export const StationPassPurchaseStatusSchema = z
    .enum(['awaiting_desk', 'awaiting_upi', 'applied', 'cancelled', 'expired'])
    .openapi('StationPassPurchaseStatus', { description: 'Where a kiosk pass purchase is (F-KIO-14).' });
/** The UPI request a kiosk shows as a QR while a `upi` purchase waits (F-KIO-14, spec/06 §9.3). */
export const StationPassPurchaseUpiSchema = z
    .object({
    /** What the kiosk renders as the QR code. */
    qrPayload: z.string(),
    /** The `upi://pay…` deep link the QR encodes. */
    upiIntentUrl: z.string(),
    expiresAt: IsoDateTimeSchema,
})
    .openapi('StationPassPurchaseUpi', { description: 'The UPI QR a kiosk shows for a pass purchase (F-KIO-14).' });
/**
 * "Scan & pay (UPI)" at the PC (F-PAY-02, F-KIO-10, F-KIO-14; spec/06 §8.3, 2026-10-01): a desk sale, top-up or purchase
 * the player pays by scanning **the venue's own UPI QR** on the kiosk — the counter-UPI QR Sell shows, moved to the PC. It
 * is a desk tender in every other way: nothing is recorded until staff at the desk confirm the payment arrived (a
 * `counter_upi` tender, audited as at the counter). The kiosk draws the QR itself from these fields, exactly
 * `upi://pay?pa=<vpa>&pn=<payeeName>&am=<amount in rupees, 2 decimals>&cu=INR&tn=<reference>` (`upiPayUri` in
 * `@clavian/domain`), so the reference rides in the payment's note and the desk can match it. Present only while it
 * waits on the desk; absent otherwise (never `null` from the api; a station may forward it as `null`).
 */
export const StationScanToPaySchema = z
    .object({
    /** The venue's UPI ID (`payments.counterUpiVpa`), resolved for the station's venue. */
    vpa: z.string().min(3),
    /** The name the UPI app shows the player: the venue's. */
    payeeName: z.string(),
    /** What to pay: the sale's outstanding (after any wallet part), or the top-up's amount. */
    amount: PositiveMoneySchema,
    /** A short code for the desk to match the payment by ("7K3QPM"); also the UPI note (`tn`). */
    reference: z.string().min(4).max(16),
})
    .openapi('StationScanToPay', { description: 'The venue’s own UPI QR a kiosk shows for a desk payment scanned at the PC (F-PAY-02).' });
/** A pass bought from the kiosk (F-KIO-14): what was bought, how it is paid and where it is. */
export const StationPassPurchaseSchema = z
    .object({
    id: PassPurchaseIdSchema,
    productId: PassProductIdSchema,
    /** The pass name, as sold. */
    name: z.string(),
    durationSeconds: z.int().min(0),
    /** What the sale charges, priced by the server from the product's live version. */
    price: MoneySchema,
    tender: StationPassTenderSchema,
    status: StationPassPurchaseStatusSchema,
    /** The kiosk order in the console's Orders. */
    saleId: SaleIdSchema,
    /** The pass holding, once issued. */
    holdingId: PassHoldingIdSchema.nullable(),
    /**
     * The seconds queued onto the running session when it was applied; `0` when the pass cannot fund play
     * on this station now (outside its hours, or the session had ended) and waits on the player's account;
     * `null` until applied.
     */
    queuedSeconds: z.int().min(0).nullable(),
    /** The QR to show while an `upi` purchase is `awaiting_upi`; `null` otherwise. */
    upi: StationPassPurchaseUpiSchema.nullable(),
    /** The venue's own UPI QR while a "Scan & pay" purchase is `awaiting_desk` (F-PAY-02); absent otherwise. */
    scanToPay: StationScanToPaySchema.optional(),
    /** What the wallet has paid towards it — the wallet part of a split tender (F-KIO-14), or the whole of a wallet buy. */
    walletPaid: MoneySchema,
    /** What the sale still owes: the desk's or the UPI QR's part; zero once paid (the sale's outstanding, F-KIO-14). */
    outstanding: MoneySchema,
    createdAt: IsoDateTimeSchema,
    appliedAt: IsoDateTimeSchema.nullable(),
})
    .openapi('StationPassPurchase', { description: 'A pass bought from the kiosk (F-KIO-14).' });
/**
 * Buy a pass at the kiosk: how it is paid, and optionally a discount code (F-POS-02) applied to the sale before it
 * is paid — a use of the code is reserved and the pass costs the discounted total. The product is in the path; the
 * price is the server's.
 */
export const PurchaseStationPassRequestSchema = z
    .object({
    tender: StationPassTenderSchema,
    discountCode: z.string().min(4).max(24).optional(),
    /**
     * Split tender (F-KIO-14, spec/06 §8.3): take this much from the player's wallet first, and the rest by `tender`
     * (`upi`, `desk` or `on_account`). At most what the sale owes (`tender-exceeds-outstanding` otherwise); not with
     * `tender: wallet`.
     */
    walletAmount: PositiveMoneySchema.optional(),
    /**
     * The price the kiosk showed (after any code), compared — never used as a price (F-SES-17 rule 3, spec/08 §2.9):
     * when the server's total differs nothing is bought and the answer is `409 price-changed` with the fresh `total`.
     */
    expectedTotal: MoneySchema.optional(),
    /**
     * "Scan & pay (UPI)" (F-PAY-02, 2026-10-01): with `tender: desk`, the player pays by scanning the venue's own UPI QR at
     * the PC; the purchase carries `scanToPay` while it waits on the desk. Ignored with any other tender, and when the
     * venue has no UPI ID (it is then an ordinary desk purchase).
     */
    scanToPay: z.boolean().optional(),
})
    .strict()
    .openapi('PurchaseStationPassRequest', { description: 'Buy a pass from the kiosk (F-KIO-14).' });
/**
 * The `station.pass.purchase` command payload (spec/09 §6.2, F-KIO-14): a kiosk pass purchase moved —
 * applied after staff took payment or the UPI capture landed, cancelled, or expired. The station forwards it
 * to the kiosk and, once `applied`, reads the purchase back so the api re-sends the session lease.
 */
export const StationPassPurchaseCommandSchema = z
    .object({ purchase: StationPassPurchaseSchema })
    .openapi('StationPassPurchaseCommand', { description: 'The `station.pass.purchase` command payload (spec/09 §6.2).' });
const STATION_PURCHASE_AUTH = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5). The station provides its own ' +
    'organisation, venue and station from the token; the purchase is for the player on its own active session.';
const StationPassProductParamsSchema = z.object({ productId: PassProductIdSchema }).openapi('StationPassProductParams');
const StationPassPurchaseParamsSchema = z.object({ purchaseId: PassPurchaseIdSchema }).openapi('StationPassPurchaseParams');
export const purchaseStationPassRoute = createRoute({
    method: 'post',
    path: '/v1/station/passes/{productId}/purchase',
    operationId: 'purchaseStationPass',
    tags: ['passes', 'stations'],
    summary: 'Buy a pass from the kiosk (F-KIO-14)',
    description: `${STATION_PURCHASE_AUTH} Creates a kiosk sale for the pass, priced from the product's live version, and ` +
        'pays it by the tender: `wallet` and `on_account` pay now and the pass is issued and queued onto the ' +
        'session at once (the new lease is pushed); `desk` leaves an unpaid order for the counter; `upi` creates a ' +
        'payment intent and returns its QR. Only a product sold on the `kiosk` channel, on sale now and in scope for ' +
        'this venue can be bought. A pass for another class than this station’s is sold (warn-and-allow, F-SES-17 rule 4): ' +
        'it waits on the player’s account and funds no play on this PC (`queuedSeconds` 0). An `expectedTotal` that ' +
        'differs from the server’s total is `409 price-changed`. Requires an `Idempotency-Key` — a retry ' +
        'answers the purchase already made, so a double tap never charges twice. Audited.',
    request: {
        params: StationPassProductParamsSchema,
        headers: IdempotentCreateHeaderSchema,
        body: { content: { 'application/json': { schema: PurchaseStationPassRequestSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(StationPassPurchaseSchema, 'The purchase: applied, or waiting for the desk or the UPI payment.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('The station has no active session, no such pass product, or no such discount code.', 'not-found', 'discount-code-unknown'),
        409: problemResponse('The pass cannot be bought here, the tender cannot pay for it, or the discount code does not apply.', 'pass-not-buyable-here', 'pass-product-inactive', 'pass-purchase-guest', 'wallet-disabled', 'wallet-insufficient-funds', 'credit-disabled', 'credit-limit-exceeded', 'online-payments-unavailable', 'session-already-ended', 'discount-code-inactive', 'discount-code-expired', 'discount-code-minimum', 'discount-limit-reached', 'tender-exceeds-outstanding', 'price-changed'),
        422: problemResponse('The same key was already used for a different purchase.', 'idempotency-key-reused', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getStationPassPurchaseRoute = createRoute({
    method: 'get',
    path: '/v1/station/passes/purchases/{purchaseId}',
    operationId: 'getStationPassPurchase',
    tags: ['passes', 'stations'],
    summary: 'Read a kiosk pass purchase (F-KIO-14)',
    description: `${STATION_PURCHASE_AUTH} Returns where the purchase is. An \`awaiting_upi\` purchase whose request has ` +
        'expired or failed is closed as `expired` here. Once `applied`, the session lease is re-sent to the station ' +
        'so the kiosk shows the queued time. Only a purchase made on this station is visible.',
    request: { params: StationPassPurchaseParamsSchema },
    responses: {
        200: jsonResponse(StationPassPurchaseSchema, 'The purchase.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('No such purchase on this station.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const cancelStationPassPurchaseRoute = createRoute({
    method: 'post',
    path: '/v1/station/passes/purchases/{purchaseId}/cancel',
    operationId: 'cancelStationPassPurchase',
    tags: ['passes', 'stations'],
    summary: 'Cancel a pending kiosk pass purchase (F-KIO-14)',
    description: `${STATION_PURCHASE_AUTH} The player cancels a purchase that is waiting for the desk or for the UPI ` +
        'payment (or the kiosk closes an expired QR): the open payment intent and the unpaid sale are cancelled and ' +
        'nothing is applied. A purchase already cancelled or expired is answered as it is; an applied one is refused. ' +
        'Audited.',
    request: { params: StationPassPurchaseParamsSchema },
    responses: {
        200: jsonResponse(StationPassPurchaseSchema, 'The purchase, cancelled.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('No such purchase on this station.', 'not-found'),
        409: problemResponse('The purchase is already paid and applied.', 'pass-purchase-closed'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------------------------
/** Make a pass product: its terms, plus the display fields (spec/06 §3.1). */
export const CreatePassProductRequestSchema = PassProductTermsSchema.extend({
    /** 0–7 windows; empty (the default) means any time (spec/06 §3.1). */
    windows: z.array(TimeWindowSchema).max(7).default([]),
    label: PassLabelSchema.default('standard'),
    channels: z.array(PassChannelSchema).min(1).max(4).default(['counter']),
    active: z.boolean().default(true),
    sortOrder: z.int().min(0).max(100_000).default(0),
    accentToken: z.string().trim().min(1).max(64).optional(),
})
    .strict()
    .openapi('CreatePassProductRequest', { description: 'Create a pass product (F-PRC-01).' });
/**
 * Edit a pass product. A change to any term makes a new version (existing holdings keep theirs,
 * spec/06 §3.1); `active`, `sortOrder` and `accentToken` change in place. Every field is optional so a
 * caller sends only what it changes.
 */
export const UpdatePassProductRequestSchema = PassProductTermsSchema.partial()
    .extend({
    active: z.boolean().optional(),
    sortOrder: z.int().min(0).max(100_000).optional(),
    accentToken: z.string().trim().min(1).max(64).nullable().optional(),
})
    .strict()
    .openapi('UpdatePassProductRequest', { description: 'Edit a pass product; a term change makes a new version (F-PRC-01).' });
/** Sell a paid holding to a player, paid by a tender (spec/06 §3.2). */
export const SellPassRequestSchema = z
    .object({ playerId: PlayerIdSchema, tender: PassTenderSchema })
    .strict()
    .openapi('SellPassRequest', { description: 'Sell a pass to a player at the counter (F-PRC-01/-02).' });
/** Staff grant a free holding, with a reason (spec/06 §3.2, F-PRC-06). */
export const GrantPassRequestSchema = z
    .object({ playerId: PlayerIdSchema, reason: z.string().trim().min(1).max(280) })
    .strict()
    .openapi('GrantPassRequest', { description: 'Staff grant a pass to a player, with a reason (F-PRC-06).' });
/** Refund a holding, returning money to a tender (spec/06 §3.4, F-PRC-04). */
export const RefundPassRequestSchema = z
    .object({
    tender: PassTenderSchema,
    /** Required to use a refund override when the rules would otherwise refuse it (spec/06 §3.4). */
    reason: z.string().trim().min(1).max(280).optional(),
})
    .strict()
    .openapi('RefundPassRequest', { description: 'Refund a pass holding (F-PRC-04).' });
/** Void a holding, removing unused seconds; a reason is required (spec/06 §3.4, F-PRC-05). */
export const VoidPassRequestSchema = z
    .object({ reason: z.string().trim().min(1).max(280) })
    .strict()
    .openapi('VoidPassRequest', { description: 'Void a pass holding (F-PRC-05).' });
/** Filters for the holdings list (spec/06 §3.2). */
export const PassHoldingQuerySchema = PageQuerySchema.extend({
    productId: PassProductIdSchema.optional(),
    playerId: PlayerIdSchema.optional(),
    status: PassHoldingStatusSchema.optional(),
}).openapi('PassHoldingQuery');
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
const ProductParamsSchema = OrganizationScopeParamsSchema.extend({ productId: PassProductIdSchema }).openapi('PassProductParams');
const HoldingParamsSchema = OrganizationScopeParamsSchema.extend({ holdingId: PassHoldingIdSchema }).openapi('PassHoldingParams');
const productNotFound = problemResponse('No such organisation, or no such pass product.', 'not-found');
const holdingNotFound = problemResponse('No such organisation, or no such pass holding.', 'not-found');
export const listPassProductsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/passes/products',
    operationId: 'listPassProducts',
    tags: ['passes'],
    summary: 'The pass catalogue (F-PRC-01)',
    description: 'Every pass product of the organisation, with its live version and today’s count at the venue named by ' +
        '`X-Clavian-Venue` (zero when none is named). Requires `passes:read`.',
    [PERMISSION_EXTENSION]: PASSES_READ,
    request: { params: OrganizationScopeParamsSchema, query: PageQuerySchema },
    responses: {
        200: jsonResponse(PassProductListSchema, 'A page of pass products.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createPassProductRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/passes/products',
    operationId: 'createPassProduct',
    tags: ['passes'],
    summary: 'Make a pass product (F-PRC-01)',
    description: 'Creates a pass product and its first version. Its price, duration, classes and windows are the terms a sale ' +
        'snapshots. Requires `passes:manage`. Audited. Catalogue configuration, not a value movement, so no ' +
        '`Idempotency-Key` — a duplicate product is edited or archived, never a double charge.',
    [PERMISSION_EXTENSION]: PASSES_MANAGE,
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { 'application/json': { schema: CreatePassProductRequestSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(PassProductSchema, 'The pass product, with its first version.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getPassProductRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/passes/products/{productId}',
    operationId: 'getPassProduct',
    tags: ['passes'],
    summary: 'One pass product (F-PRC-01)',
    description: 'A pass product with its live version. Requires `passes:read`.',
    [PERMISSION_EXTENSION]: PASSES_READ,
    request: { params: ProductParamsSchema },
    responses: {
        200: jsonResponse(PassProductSchema, 'The pass product.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: productNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updatePassProductRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/passes/products/{productId}',
    operationId: 'updatePassProduct',
    tags: ['passes'],
    summary: 'Edit a pass product (F-PRC-01)',
    description: 'Changing the price, duration, classes, windows or any other term makes a new version; existing holdings keep ' +
        'the version they were sold under. `active`, `sortOrder` and `accentToken` change in place. Requires ' +
        '`passes:manage`. Audited.',
    [PERMISSION_EXTENSION]: PASSES_MANAGE,
    request: {
        params: ProductParamsSchema,
        body: { content: { 'application/json': { schema: UpdatePassProductRequestSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(PassProductSchema, 'The pass product, with the change.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: productNotFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const sellPassRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/passes/products/{productId}/sell',
    operationId: 'sellPass',
    tags: ['passes'],
    summary: 'Sell a pass to a player (F-PRC-01/-02)',
    description: 'Sells a holding of the product’s live version to the player, paid by a tender at the venue named by ' +
        '`X-Clavian-Venue`, and grants its seconds. Money moves through the ledger; the holding’s expiry is set ' +
        'from the version’s validity. Requires `orders:create` and an `Idempotency-Key`; a retry with the same key ' +
        'answers the sale already made. Audited. **Superseded for the console (2026-09-30):** a counter pass sale is a ' +
        '`createSale` pass line (`passProductId`), so it is a sale in Orders with its tenders, receipt and discount ' +
        'codes; this door records no sale and stays only for existing API callers.',
    [PERMISSION_EXTENSION]: ORDERS_CREATE,
    request: {
        params: ProductParamsSchema,
        headers: IdempotentCreateHeaderSchema,
        body: { content: { 'application/json': { schema: SellPassRequestSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(PassHoldingSchema, 'The holding the sale created.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation, pass product, player, or venue you can see.', 'not-found'),
        409: problemResponse('The pass product is not on sale, GST on passes is on here (`tax.playTaxed`): sell it at Sell, so its tax invoice is issued — or cash is taken by someone holding no open drawer (ADR-0031).', 'pass-product-inactive', 'conflict', 'cash-needs-shift'),
        422: problemResponse('The same key was already used for a different sale.', 'idempotency-key-reused', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const grantPassRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/passes/products/{productId}/grant',
    operationId: 'grantPass',
    tags: ['passes'],
    summary: 'Staff grant a pass (F-PRC-06)',
    description: 'Grants a free holding of the product’s live version to the player, with a reason, recorded as a zero-value ' +
        'order so it appears in reports. Requires `passes:grant` and an `Idempotency-Key`. Audited.',
    [PERMISSION_EXTENSION]: PASSES_GRANT,
    request: {
        params: ProductParamsSchema,
        headers: IdempotentCreateHeaderSchema,
        body: { content: { 'application/json': { schema: GrantPassRequestSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(PassHoldingSchema, 'The granted holding.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation, pass product or player.', 'not-found'),
        409: problemResponse('The pass product is not on sale.', 'pass-product-inactive'),
        422: problemResponse('The same key was already used for a different grant.', 'idempotency-key-reused', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listPassHoldingsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/passes/holdings',
    operationId: 'listPassHoldings',
    tags: ['passes'],
    summary: 'Pass holdings (F-PRC-02)',
    description: 'The organisation’s pass holdings, newest first, filterable by product, player and status. Remaining seconds ' +
        'are read from the ledger. Requires `passes:read`.',
    [PERMISSION_EXTENSION]: PASSES_READ,
    request: { params: OrganizationScopeParamsSchema, query: PassHoldingQuerySchema },
    responses: {
        200: jsonResponse(PassHoldingListSchema, 'A page of holdings.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const refundPassHoldingRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/passes/holdings/{holdingId}/refund',
    operationId: 'refundPassHolding',
    tags: ['passes'],
    summary: 'Refund a pass holding (F-PRC-04)',
    description: 'Refunds the holding by its rules: a full refund inside the window, else pro-rata on the remaining seconds ' +
        'if enabled, else refused unless a reason invokes the refund override. Money returns through the tender; the ' +
        'holding becomes `refunded` with its remaining seconds removed. Requires `passes:refund`. Audited.',
    [PERMISSION_EXTENSION]: PASSES_REFUND,
    request: {
        params: HoldingParamsSchema,
        body: { content: { 'application/json': { schema: RefundPassRequestSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(PassHoldingSchema, 'The holding, now refunded.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: holdingNotFound,
        409: problemResponse('The holding cannot be refunded under its rules.', 'pass-not-refundable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const voidPassHoldingRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/passes/holdings/{holdingId}/void',
    operationId: 'voidPassHolding',
    tags: ['passes'],
    summary: 'Void a pass holding (F-PRC-05)',
    description: 'Removes the holding’s unused seconds and returns no money; a reason is required. Refused once any second has ' +
        'been played — refund instead (spec/06 §3.4). The holding becomes `voided`. Requires `passes:void`. Audited.',
    [PERMISSION_EXTENSION]: PASSES_VOID,
    request: {
        params: HoldingParamsSchema,
        body: { content: { 'application/json': { schema: VoidPassRequestSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(PassHoldingSchema, 'The holding, now voided.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: holdingNotFound,
        409: problemResponse('The holding cannot be voided; refund it instead.', 'pass-not-voidable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const PASSES_ROUTES = [
    listPassProductsRoute,
    createPassProductRoute,
    getPassProductRoute,
    updatePassProductRoute,
    sellPassRoute,
    grantPassRoute,
    listPassHoldingsRoute,
    refundPassHoldingRoute,
    voidPassHoldingRoute,
];
//# sourceMappingURL=passes.js.map